import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, MapPin, Search, Navigation, Locate, Map, List, DollarSign } from 'lucide-react';
import { BrickRatingCompact, BrickRatingDisplay } from '@/components/BrickRating';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Bogotá center coordinates as default
const BOGOTA_CENTER = { lat: 4.7110, lng: -74.0721 };

// Fix for default marker icons in Leaflet with React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom worker marker icon
const createWorkerIcon = (rating) => {
  const color = rating >= 4 ? '#22C55E' : rating >= 2.5 ? '#F59E0B' : '#3B82F6';
  
  return L.divIcon({
    className: 'worker-marker',
    html: `<div style="
      background-color: ${color};
      width: 36px;
      height: 36px;
      border-radius: 50%;
      border: 3px solid white;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      display: flex;
      align-items: center;
      justify-content: center;
    ">
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="white" stroke="white" stroke-width="2">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
        <circle cx="12" cy="7" r="4"></circle>
      </svg>
    </div>`,
    iconSize: [36, 36],
    iconAnchor: [18, 36],
    popupAnchor: [0, -36]
  });
};

export default function WorkerSearch({ user, token }) {
  const navigate = useNavigate();
  const [workers, setWorkers] = useState([]);
  const [filteredWorkers, setFilteredWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [userLocation, setUserLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [maxDistance, setMaxDistance] = useState('all');
  const [viewMode, setViewMode] = useState('map'); // 'map' or 'list'

  const categories = [
    { value: 'all', label: 'Todas las categorías' },
    { value: 'plomeria', label: 'Plomería' },
    { value: 'electricidad', label: 'Electricidad' },
    { value: 'pintura', label: 'Pintura' },
    { value: 'albanileria', label: 'Albañilería' },
    { value: 'ingeniero_civil', label: 'Ingeniero Civil' },
    { value: 'arquitecto', label: 'Arquitecto' },
    { value: 'topografia', label: 'Topografía' },
    { value: 'cerrajeria', label: 'Cerrajería' },
    { value: 'planos_disenos', label: 'Planos y Diseños' },
    { value: 'licencias', label: 'Licencias de Construcción' }
  ];

  const distanceOptions = [
    { value: 'all', label: 'Cualquier distancia' },
    { value: '5', label: 'Hasta 5 km' },
    { value: '10', label: 'Hasta 10 km' },
    { value: '20', label: 'Hasta 20 km' },
    { value: '50', label: 'Hasta 50 km' }
  ];

  useEffect(() => {
    // Try to get user location on component mount
    getUserLocation();
  }, []);

  useEffect(() => {
    fetchWorkers();
  }, [userLocation, maxDistance]);

  useEffect(() => {
    filterWorkers();
  }, [workers, searchTerm, selectedCategory]);

  const getUserLocation = () => {
    setLocationLoading(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
          setLocationLoading(false);
          toast.success('Ubicación detectada');
        },
        (error) => {
          console.log('Geolocation error:', error);
          // Use Bogotá center as default
          setUserLocation(BOGOTA_CENTER);
          setLocationLoading(false);
          toast.info('Usando ubicación predeterminada: Bogotá Centro');
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } else {
      setUserLocation(BOGOTA_CENTER);
      setLocationLoading(false);
      toast.info('Geolocalización no disponible. Usando Bogotá Centro.');
    }
  };

  const fetchWorkers = async () => {
    try {
      setLoading(true);
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      // Build query parameters
      let url = `${API}/workers`;
      const params = new URLSearchParams();
      
      if (userLocation) {
        params.append('lat', userLocation.lat);
        params.append('lng', userLocation.lng);
      }
      
      if (maxDistance !== 'all') {
        params.append('max_distance', maxDistance);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await axios.get(url, config);
      setWorkers(response.data);
      setFilteredWorkers(response.data);
    } catch (error) {
      toast.error('Error al cargar trabajadores');
    } finally {
      setLoading(false);
    }
  };

  const filterWorkers = () => {
    let filtered = [...workers];

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(worker =>
        worker.specialties?.some(s => s.toLowerCase().includes(search)) ||
        worker.bio?.toLowerCase().includes(search)
      );
    }

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(worker =>
        worker.specialties?.includes(selectedCategory)
      );
    }

    setFilteredWorkers(filtered);
  };

  const getCategoryLabel = (value) => {
    const cat = categories.find(c => c.value === value);
    return cat ? cat.label : value;
  };

  // Prepare workers with valid coordinates
  const workersWithCoords = filteredWorkers.filter(worker => 
    worker.location?.lat && worker.location?.lng
  );

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <div className="bg-[#3A4F6C] text-white p-4 sticky top-0 z-[1000]">
        <div className="container mx-auto">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard')}
              className="text-white hover:bg-white/20"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-xl font-bold">BUSCAR TRABAJADORES</h1>
          </div>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              data-testid="search-input"
              placeholder="Buscar por especialidad o descripción..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white text-slate-900"
            />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b shadow-sm p-4 sticky top-[120px] z-[999]">
        <div className="container mx-auto space-y-3">
          {/* Location and Distance Row */}
          <div className="flex items-center gap-3">
            <Button
              data-testid="locate-me-button"
              variant="outline"
              size="sm"
              onClick={getUserLocation}
              disabled={locationLoading}
              className="flex items-center gap-2"
            >
              <Locate className={`h-4 w-4 ${locationLoading ? 'animate-spin' : ''}`} />
              {locationLoading ? 'Localizando...' : 'Mi ubicación'}
            </Button>
            
            {userLocation && (
              <span className="text-xs text-green-600 flex items-center gap-1">
                <Navigation className="h-3 w-3" />
                {userLocation === BOGOTA_CENTER ? 'Bogotá Centro' : 'Ubicación actual'}
              </span>
            )}
            
            <Select value={maxDistance} onValueChange={setMaxDistance}>
              <SelectTrigger data-testid="distance-select" className="w-40">
                <SelectValue placeholder="Distancia" />
              </SelectTrigger>
              <SelectContent>
                {distanceOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Category Filter */}
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger data-testid="category-select" className="w-full">
              <SelectValue placeholder="Seleccionar categoría" />
            </SelectTrigger>
            <SelectContent>
              {categories.map(cat => (
                <SelectItem key={cat.value} value={cat.value}>
                  {cat.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* View Toggle */}
          <div className="flex justify-between items-center">
            <span className="text-sm text-slate-500">
              {filteredWorkers.length} trabajador{filteredWorkers.length !== 1 ? 'es' : ''} encontrado{filteredWorkers.length !== 1 ? 's' : ''}
            </span>
            <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
              <Button
                variant={viewMode === 'map' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('map')}
                className={viewMode === 'map' ? 'bg-orange-500 hover:bg-orange-600' : ''}
              >
                <Map className="h-4 w-4 mr-1" />
                Mapa
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className={viewMode === 'list' ? 'bg-orange-500 hover:bg-orange-600' : ''}
              >
                <List className="h-4 w-4 mr-1" />
                Lista
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto p-4">
        {loading ? (
          <div className="text-center py-12">
            <p className="text-slate-500">Cargando trabajadores...</p>
          </div>
        ) : filteredWorkers.length === 0 ? (
          <div className="text-center py-12">
            <MapPin className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 text-lg mb-2">No se encontraron trabajadores</p>
            <p className="text-sm text-slate-400">Intenta cambiar los filtros de búsqueda</p>
          </div>
        ) : viewMode === 'map' ? (
          /* Map View */
          <div className="rounded-xl overflow-hidden shadow-lg border-2 border-slate-200" style={{ height: '60vh' }}>
            <MapContainer
              center={[userLocation?.lat || BOGOTA_CENTER.lat, userLocation?.lng || BOGOTA_CENTER.lng]}
              zoom={12}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              
              {/* User location marker */}
              {userLocation && (
                <Marker
                  position={[userLocation.lat, userLocation.lng]}
                  icon={L.divIcon({
                    className: 'user-marker',
                    html: `<div style="
                      background-color: #3B82F6;
                      width: 20px;
                      height: 20px;
                      border-radius: 50%;
                      border: 4px solid white;
                      box-shadow: 0 0 0 2px #3B82F6, 0 2px 8px rgba(0,0,0,0.3);
                    "></div>`,
                    iconSize: [20, 20],
                    iconAnchor: [10, 10]
                  })}
                >
                  <Popup>
                    <strong>Tu ubicación</strong>
                  </Popup>
                </Marker>
              )}
              
              {/* Worker markers */}
              {workersWithCoords.map((worker) => (
                <Marker
                  key={worker.id}
                  position={[worker.location.lat, worker.location.lng]}
                  icon={createWorkerIcon(worker.rating)}
                >
                  <Popup>
                    <div className="min-w-[220px]">
                      <div className="flex items-center gap-3 mb-2">
                        <Avatar className="h-12 w-12 border-2 border-orange-300">
                          <AvatarImage src={worker.profile_image ? `${BACKEND_URL}${worker.profile_image}?auth=${token}` : undefined} />
                          <AvatarFallback className="bg-orange-500 text-white font-bold">
                            {worker.user_id?.substring(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <h3 className="font-bold text-sm">Trabajador #{worker.id.substring(0, 8)}</h3>
                          <BrickRatingCompact rating={worker.rating} totalJobs={worker.total_jobs} />
                        </div>
                      </div>
                      
                      <p className="text-sm text-slate-600 mb-2 line-clamp-2">{worker.bio}</p>
                      
                      <div className="flex flex-wrap gap-1 mb-2">
                        {worker.specialties?.slice(0, 2).map((spec, idx) => (
                          <Badge key={idx} className="bg-orange-100 text-orange-700 text-xs">
                            {getCategoryLabel(spec)}
                          </Badge>
                        ))}
                      </div>
                      
                      <div className="flex justify-between items-center mb-3">
                        <span className="text-sm text-slate-500 flex items-center">
                          <MapPin className="h-3 w-3 mr-1" />
                          {worker.location?.address?.substring(0, 15)}...
                        </span>
                        {worker.distance !== undefined && worker.distance < 999 && (
                          <Badge className="bg-blue-100 text-blue-700 text-xs">
                            {worker.distance < 1 
                              ? `${Math.round(worker.distance * 1000)}m` 
                              : `${worker.distance.toFixed(1)} km`}
                          </Badge>
                        )}
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-orange-600">
                          ${worker.hourly_rate?.toLocaleString()}/hora
                        </span>
                        <Button
                          size="sm"
                          className="bg-orange-500 hover:bg-orange-600 text-white"
                          onClick={() => navigate(`/worker/${worker.id}`)}
                        >
                          Ver Perfil
                        </Button>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
            
            {/* Legend */}
            <div className="absolute bottom-4 left-4 bg-white rounded-lg shadow-lg p-3 z-[1000]">
              <p className="text-xs font-semibold mb-2">Calificación:</p>
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-xs">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span>4+ Excelente</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                  <span>2.5-4 Bueno</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  <span>Nuevo</span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* List View */
          <div className="space-y-4">
            {filteredWorkers.map((worker) => (
              <Card
                key={worker.id}
                data-testid={`worker-card-${worker.id}`}
                className="cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-l-orange-500"
                onClick={() => navigate(`/worker/${worker.id}`)}
              >
                <CardContent className="p-4">
                  <div className="flex gap-4">
                    <Avatar className="h-16 w-16">
                      <AvatarImage src={worker.profile_image ? `${BACKEND_URL}${worker.profile_image}?auth=${token}` : undefined} />
                      <AvatarFallback className="bg-orange-500 text-white text-xl font-bold">
                        {worker.user_id?.substring(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>

                    <div className="flex-1">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h3 className="font-bold text-lg">Trabajador #{worker.id.substring(0, 8)}</h3>
                          <div className="mt-1">
                            <BrickRatingCompact rating={worker.rating} totalJobs={worker.total_jobs} />
                          </div>
                        </div>
                      </div>

                      <p className="text-sm text-slate-600 line-clamp-2 mb-2">{worker.bio}</p>

                      <div className="flex flex-wrap gap-1 mb-3">
                        {worker.specialties?.map((specialty, idx) => (
                          <Badge key={idx} className="bg-orange-100 text-orange-700 text-xs">
                            {getCategoryLabel(specialty)}
                          </Badge>
                        ))}
                      </div>

                      <div className="flex justify-between items-center">
                        <div className="flex items-center text-slate-500 text-sm">
                          <MapPin className="h-4 w-4 mr-1" />
                          <span className="truncate max-w-[150px]">
                            {worker.location?.address || 'Ubicación no especificada'}
                          </span>
                          {worker.distance !== undefined && worker.distance < 999 && (
                            <Badge className="ml-2 bg-blue-100 text-blue-700 text-xs">
                              {worker.distance < 1 
                                ? `${Math.round(worker.distance * 1000)}m` 
                                : `${worker.distance.toFixed(1)} km`}
                            </Badge>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-slate-500">Desde</p>
                          <p className="font-bold text-orange-600 text-lg">
                            ${worker.hourly_rate?.toLocaleString()}/hora
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Custom CSS for markers */}
      <style>{`
        .worker-marker, .user-marker {
          background: transparent;
          border: none;
        }
        .leaflet-popup-content-wrapper {
          border-radius: 12px;
        }
        .leaflet-popup-content {
          margin: 12px;
        }
      `}</style>
    </div>
  );
}
